import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { ChecklistCategory, ChecklistItemDTO } from "@shared/types";
import { CHECKLIST_CATEGORIES } from "@shared/types";
import { fmtDayShort, todayLocal } from "@/lib/format";
import { CHECK_CAT } from "@/lib/meta";
import { toast } from "@/lib/store";
import { tripApi, type Bundle, type Derived } from "@/lib/trip";
import { Avatar, cx, Input, Select } from "../ui";

export function ChecklistPanel({ b, d }: { b: Bundle; d: Derived }) {
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState<ChecklistCategory>("booking");
  const [hideDone, setHideDone] = useState(false);
  const canEdit = b.trip.myRole !== "viewer";
  const today = todayLocal();
  const done = b.checklist.filter((c) => c.done).length;

  const add = async () => {
    if (!title.trim()) return;
    try {
      await tripApi.createCheck(b.trip.id, { title: title.trim(), category: cat });
      setTitle("");
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    }
  };
  const upd = (c: ChecklistItemDTO, p: Partial<ChecklistItemDTO>) => tripApi.updateCheck(b.trip.id, c.id, p).catch((e) => toast((e as Error).message, { tone: "error" }));

  return (
    <div className="pb-6">
      <div className="mb-3 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div className="h-full rounded-full bg-[var(--accent)] transition-all" style={{ width: `${b.checklist.length ? (done / b.checklist.length) * 100 : 0}%` }} />
        </div>
        <span className="text-[13px] font-bold tabular-nums">
          {done}/{b.checklist.length}
        </span>
        <button className="text-[12px] font-semibold text-[var(--accent)]" onClick={() => setHideDone(!hideDone)}>
          {hideDone ? "показать все" : "скрыть сделанное"}
        </button>
      </div>
      {canEdit && (
        <div className="mb-4 flex gap-2">
          <Select className="w-auto shrink-0" value={cat} onChange={(e) => setCat(e.target.value as ChecklistCategory)}>
            {CHECKLIST_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CHECK_CAT[c].emoji}
              </option>
            ))}
          </Select>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Новая задача…" />
          <button onClick={add} className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--accent)] text-[var(--accent-fg)]" aria-label="Добавить">
            <Plus size={18} />
          </button>
        </div>
      )}
      {CHECKLIST_CATEGORIES.map((c) => {
        const list = b.checklist.filter((x) => x.category === c && (!hideDone || !x.done));
        if (!list.length) return null;
        return (
          <section key={c} className="mb-4">
            <h3 className="mb-1 text-[12px] font-extrabold tracking-wider text-muted uppercase">
              {CHECK_CAT[c].emoji} {CHECK_CAT[c].label}
            </h3>
            {list.map((x) => {
              const overdue = !x.done && x.dueDate && x.dueDate < today;
              const assignee = x.assigneeId ? d.members.get(x.assigneeId) : null;
              return (
                <div key={x.id} className="group flex items-start gap-2.5 rounded-xl px-2 py-1.5 hover:bg-[var(--surface-2)]">
                  <input type="checkbox" disabled={!canEdit} checked={x.done} onChange={(e) => upd(x, { done: e.target.checked })} className="mt-1 size-4 shrink-0 accent-[var(--accent)]" />
                  <div className="min-w-0 flex-1">
                    <div className={cx("text-[14px] leading-snug", x.done && "text-muted line-through")}>{x.title}</div>
                    {x.notes && <div className="text-[12px] text-muted">{x.notes}</div>}
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                      {x.dueDate && <span className={cx("font-semibold", overdue ? "text-[var(--danger)]" : "text-muted")}>до {fmtDayShort(x.dueDate)}</span>}
                      {canEdit && (
                        <select
                          value={x.assigneeId ?? ""}
                          onChange={(e) => upd(x, { assigneeId: e.target.value || null })}
                          className="rounded-md bg-transparent text-[11px] text-muted outline-none hover:text-[var(--fg)]"
                        >
                          <option value="">кто делает?</option>
                          {b.members.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name ?? m.email}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {assignee && <Avatar m={assignee} size={22} />}
                  {canEdit && (
                    <button
                      className="mt-0.5 text-muted opacity-0 group-hover:opacity-100 hover:text-[var(--danger)]"
                      aria-label="Удалить"
                      onClick={async () => {
                        const r = await tripApi.deleteCheck(b.trip.id, x.id);
                        toast("Задача удалена", { action: r.activityId ? { label: "Вернуть", run: () => void tripApi.undo(b.trip.id, { activityId: r.activityId! }) } : undefined });
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
